import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@apollo/client";

import { useAuthContext } from "./AuthContext";
import { setGlobalDimensions } from "../telemetry/appInsights";
import {
  CURRENT_USER,
  type CurrentUserResult,
} from "../members/graphql/currentUser";
import {
  PERSONAS,
  PERSONA_MODULES,
  ROLES,
  canSeeModule as canSeeModuleFromConfig,
  normalizePersona,
  type PersonaValue,
} from "../config/roles";

// ---------------------------------------------------------------------------
// DEV-ONLY persona override (offline testing — see CurrentUserProvider)
// ---------------------------------------------------------------------------
/**
 * localStorage key for the dev persona override. Selecting a persona in the
 * PersonaBadge switcher writes here (in dev); a `?persona=CODE` URL param wins
 * on first load and is persisted into this key.
 */
const DEV_PERSONA_STORAGE_KEY = "m360.persona";

/** All 6 personas — what a dev-overridden session can switch between offline. */
const ALL_PERSONAS = Object.values(PERSONAS) as PersonaValue[];

function isKnownPersona(value: string | null | undefined): value is PersonaValue {
  return !!value && (ALL_PERSONAS as string[]).includes(value);
}

/**
 * Read the dev persona override on first render. Tri-state:
 *   - a PersonaValue → an active dev override (seed that persona offline);
 *   - `null`         → "no persona" (allow-all) was explicitly chosen in dev;
 *   - `undefined`    → no dev intent at all → let the backend query decide.
 *
 * `?persona=CODE` wins over localStorage and is persisted. The explicit
 * "no persona" clear is in-session state only (not persisted as a value — an
 * absent key reads back as `undefined`, i.e. defer to the backend next load).
 * Returns `undefined` outside dev so production never short-circuits the query.
 */
function readDevPersonaOverride(): PersonaValue | null | undefined {
  if (!import.meta.env.DEV || typeof window === "undefined") return undefined;
  try {
    const param = new URLSearchParams(window.location.search).get("persona");
    if (param) {
      if (param === "__all__") {
        // Explicit allow-all via URL — clear any stored persona, return null.
        window.localStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
        return null;
      }
      const upper = param.toUpperCase();
      if (isKnownPersona(upper)) {
        window.localStorage.setItem(DEV_PERSONA_STORAGE_KEY, upper);
        return upper;
      }
    }
    const stored = window.localStorage.getItem(DEV_PERSONA_STORAGE_KEY);
    if (isKnownPersona(stored)) return stored;
  } catch {
    /* localStorage / URL unavailable — no override */
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

/**
 * The CurrentUser context exposed to all consumers via useCurrentUser().
 *
 * Persona is the PRIMARY access gate. Permission values are SECONDARY
 * refinements within a persona's already-granted surface.
 *
 * NOTE: `loading` is bounded — a hung `connectCurrentUser` surfaces as `error`
 * (Apollo's default timeout/network error path), never an infinite spinner.
 * Consumers of `loading: true` must render a bounded fallback (spinner with
 * a timeout or the app's existing LoadingSpinner).
 */
export interface CurrentUser {
  /** True while connectCurrentUser is in flight. */
  loading: boolean;
  /**
   * Non-null once the query resolves. Null means unauthenticated, failed, or
   * the query has not been issued (skip: !isAuthenticated).
   */
  error: Error | null;
  /** Role code string from backend (e.g. "ADMIN"). Derived from connectCurrentUser.role.code. */
  role: string | null;
  /** Convenience: role === ROLES.ADMIN */
  isAdmin: boolean;
  /** The user's currently active persona (normalized to FE PersonaValue). */
  currentPersona: string | null;
  /** All personas assigned to this user (normalized to FE PersonaValues). */
  personas: string[];
  /**
   * Ordered module list for `currentPersona` per PERSONA_MODULES (Phase 1 seed).
   * Empty array if no persona is set or persona is unrecognized.
   */
  personaModules: readonly string[];
  /**
   * Returns true if the current persona grants access to `moduleId`.
   * This is the PRIMARY module gate — call this, not PERSONA_MODULES directly.
   * Phase 2 will replace the underlying implementation with server-driven data;
   * call-site API is stable.
   */
  canSeeModule: (moduleId: string) => boolean;
  /**
   * Flattened set of permission value strings from connectCurrentUser.permissions.
   * Used as the SECONDARY gate within a persona's allowed surface.
   */
  permissionValues: Set<string>;
  /** Returns true if permissionValues contains `value`. Secondary gate. */
  hasPermission: (value: string) => boolean;
  /** Returns true if role matches the provided role string. */
  hasRole: (role: string) => boolean;
  /**
   * The raw connectCurrentUser payload, for consumers that need IPA/healthPlan/
   * lineOfBusiness data-scope checks from permissionMeta.
   * Null while loading or on error.
   */
  raw: CurrentUserResult["connectCurrentUser"];
  /**
   * DEV-ONLY: true when a `?persona=` / localStorage override is active and the
   * `connectCurrentUser` query has been short-circuited. Always false in
   * production. Lets the PersonaBadge switcher choose the offline (localStorage)
   * path over the mutation path. See CurrentUserProvider.
   */
  devOverrideActive: boolean;
  /**
   * DEV-ONLY: seed the active persona offline (no backend). Pass a PersonaValue
   * to activate that persona, or `null` for "no persona" (allow-all). No-op in
   * production. PersonaBadge calls this instead of the mutation when
   * `devOverrideActive` is true.
   */
  setDevPersona: (persona: PersonaValue | null) => void;
}

// ---------------------------------------------------------------------------
// Context + hook
// ---------------------------------------------------------------------------

const CurrentUserCtx = createContext<CurrentUser | null>(null);

/**
 * Access the current user's role, persona, and permissions.
 * Must be called inside <CurrentUserProvider>.
 */
export function useCurrentUser(): CurrentUser {
  const ctx = useContext(CurrentUserCtx);
  if (!ctx) throw new Error("useCurrentUser must be inside <CurrentUserProvider>");
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Fetches `connectCurrentUser` once per session (after authentication) and
 * exposes role/persona/permission data to the entire app (ACT-1256 D12).
 *
 * `connectCurrentUser` is a care-management-gql proxy resolver that forwards
 * to the connect-gql AcCurrentUser type. Transport is the existing care-mgmt
 * Apollo client — no second client is required.
 *
 * Mount order: must be BELOW <Member360ApolloProvider> (needs the Apollo client)
 * and BELOW <AuthProvider> (needs isAuthenticated). In App.tsx this means:
 *   BrowserRouter → Member360ApolloProvider → CurrentUserProvider → ...
 *
 * Auth failures from this query route through the shared Apollo errorLink →
 * triggerExpiryWarning (same path as MemberProfilePage). No new logout path here.
 *
 * fetch policy: cache-first on initial load (stable mid-session), with
 * nextFetchPolicy: "cache-and-network" so revoked permissions don't persist
 * for the full cache lifetime (Rev 2 item 3).
 *
 * FAIL BEHAVIOR (Rev 2 item 5):
 * - loading: bounded by Apollo's network timeout; error surface available to consumers.
 * - error: the app shell continues to render (fail-open for base clinical routes).
 *   Admin/restricted routes should gate on loading/error state explicitly.
 * - connectCurrentUser returns null: treated same as error — canSeeModule returns false
 *   for all modules (fail-closed for unknown state).
 */
export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, accountLabel } = useAuthContext();

  // DEV-ONLY offline override. Tri-state, read once on mount:
  //   - PersonaValue → seed that persona, short-circuit the query;
  //   - null         → explicit "no persona" (allow-all), short-circuit too;
  //   - undefined    → no dev intent → run the normal backend query.
  // The QA backend does not return personas yet, so this makes persona gating
  // testable offline. Guarded on import.meta.env.DEV (readDevPersonaOverride
  // returns undefined in prod), so production behaviour is unchanged.
  const [devPersona, setDevPersonaState] = useState<
    PersonaValue | null | undefined
  >(() => readDevPersonaOverride());
  const hasDevIntent = devPersona !== undefined;

  // Persist + adopt a dev-path selection. null = explicit "no persona" clear.
  const setDevPersona = useCallback((persona: PersonaValue | null) => {
    if (!import.meta.env.DEV) return;
    try {
      if (persona) window.localStorage.setItem(DEV_PERSONA_STORAGE_KEY, persona);
      else window.localStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
    } catch {
      /* ignore storage failures */
    }
    setDevPersonaState(persona);
  }, []);

  const { data, loading, error } = useQuery<CurrentUserResult>(CURRENT_USER, {
    // Suppress the doomed query whenever a dev override is in effect.
    skip: !isAuthenticated || hasDevIntent,
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-and-network",
    // errorPolicy: "all" so partial data is still usable if one field errors
    errorPolicy: "all",
  });

  const user = data?.connectCurrentUser ?? null;

  // Normalize backend persona code strings to canonical FE PersonaValues.
  //
  // SECURITY: there are three distinct states:
  //   - null  → backend returned NO persona field at all (the QA/dev seam where
  //             connectCurrentUser doesn't yet return persona data). This is the
  //             intentional ALLOW-ALL transition path — failing closed here would
  //             hide every gated surface for real users until the backend is fully
  //             deployed. DELETE this allow-all branch only once persona data is
  //             returned on all envs.
  //   - known PersonaValue → recognized backend code, keyed in PERSONA_MODULES,
  //             gated normally (ALLOW only modules in the persona's grant set).
  //   - non-null but unrecognized code → FAIL CLOSED. We do NOT fall back to null
  //             (the allow-all seam) because that would let a backend returning an
  //             unknown/new persona code unlock every module. Instead we keep the
  //             raw string — since it won't match any PERSONA_MODULES key,
  //             canSeeModule returns false for every module (DENY).
  //
  // The actions layer (resolveModuleActions in useFeatureAccess.ts) independently
  // uses raw.currentPersona == null for its allow-all seam. Both layers must agree:
  //   "no backend persona data" → allow-all; "unrecognized code" → fail-closed.
  //
  // DEV override wins: seed the persona offline (null = explicit allow-all).
  const currentPersona = useMemo(
    () => {
      if (hasDevIntent) return devPersona ?? null;
      const raw = user?.currentPersona?.code;
      if (!raw) return null; // no persona data from backend → allow-all seam
      return normalizePersona(raw) ?? raw; // unrecognized → keep raw (DENY via canSeeModule)
    },
    [hasDevIntent, devPersona, user?.currentPersona?.code],
  );

  // In dev-override mode expose all 6 personas so the switcher can cycle them
  // without a backend; otherwise use the backend-supplied (normalized) list.
  const personas = useMemo(
    () =>
      hasDevIntent
        ? ALL_PERSONAS
        : (user?.personas ?? [])
            .map((p) => normalizePersona(p.code))
            .filter((p): p is PersonaValue => p !== undefined),
    [hasDevIntent, user?.personas],
  );

  const permissionValues = useMemo(
    () =>
      new Set(
        (user?.permissions ?? []).map((p) => p.value),
      ),
    [user?.permissions],
  );

  const personaModules: readonly string[] = useMemo(
    () => (currentPersona ? (PERSONA_MODULES[currentPersona] ?? []) : []),
    [currentPersona],
  );

  // role is the code string from the AcRole object (e.g. "ADMIN").
  const role = user?.role?.code ?? null;

  // clinic carries the IPA code (e.g. "H1234") — an opaque identifier, not a
  // display name. Non-PHI per ACT-1037; safe to emit as a custom dimension.
  const clinic =
    user?.permissions?.find((p) => p.permissionMeta?.ipas?.length)
      ?.permissionMeta?.ipas?.[0] ?? "(none)";

  // Sync global App Insights custom dimensions whenever persona / IPA / role /
  // email changes. Dims are read at emit-time in the telemetry initializer so
  // this keeps dashboard breakdowns accurate across persona switches mid-session.
  // email is the staff account email (MSAL username or B2C claim) — not member
  // PHI (ACT-1037). It lets usage queries filter/group by individual user.
  useEffect(() => {
    setGlobalDimensions({
      persona: currentPersona ?? "(none)",
      clinic,
      role: role ?? "(none)",
      email: accountLabel ?? "(none)",
    });
  }, [currentPersona, clinic, role, accountLabel]);

  const value = useMemo<CurrentUser>(
    () => ({
      // A dev override resolves synchronously — never report it as loading.
      loading: hasDevIntent ? false : loading,
      error: error ?? null,
      role,
      isAdmin: role === ROLES.ADMIN,
      currentPersona,
      personas,
      personaModules,
      canSeeModule: (moduleId: string) =>
        // Pass the role so the `admin` role grants all-access (single admin
        // signal — the old ADMIN super-persona was removed).
        canSeeModuleFromConfig(currentPersona, moduleId, { role }),
      permissionValues,
      hasPermission: (v: string) => permissionValues.has(v),
      hasRole: (r: string) => role === r,
      raw: user,
      devOverrideActive: hasDevIntent,
      setDevPersona,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasDevIntent,
      loading,
      error,
      role,
      currentPersona,
      personas,
      personaModules,
      permissionValues,
      user,
      setDevPersona,
    ],
  );

  return (
    <CurrentUserCtx.Provider value={value}>{children}</CurrentUserCtx.Provider>
  );
}
